/**
 * Manual booking-status changes from the side panel.
 *
 * The PATCH endpoint already exists (`update(...)` in
 * `BookingController.kt`). This file just typesthe mutation and the
 * UX-friendly transition matrix the dropdown menu uses.
 *
 * The matrix is a SUGGESTION — the API stays permissive. Operators are
 * trusted; mistakes are recorded in `booking_history` so the audit
 * trail tells the story.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";

export type BookingStatus =
  | "pending"
  | "confirmed"
  | "completed"
  | "canceled"
  | "no_show";

export type StatusOption = {
  /** The status to PATCH to. */
  next: BookingStatus;
  /** Menu label, e.g. "Mark Confirmed". */
  label: string;
  /** Body copy for the confirm dialog. */
  description: string;
  /** Visual cue for the menu row. */
  tone: "default" | "primary" | "warning" | "danger";
};

export function nextStatusOptions(current: BookingStatus | undefined | null): StatusOption[] {
  switch (current) {
    case "pending":
      return [
        {
          next: "confirmed",
          label: "Mark Confirmed",
          description:
            "Confirms the booking without recording a payment. The customer's slot is locked. You can still record payments later.",
          tone: "primary",
        },
        {
          next: "completed",
          label: "Mark Completed",
          description:
            "Marks the appointment as done. Use this once the booking has happened.",
          tone: "default",
        },
        {
          next: "no_show",
          label: "Mark as No-show",
          description:
            "Records that the customer didn't turn up. Use this if no payment is collected for the missed appointment.",
          tone: "warning",
        },
      ];
    case "confirmed":
      return [
        {
          next: "completed",
          label: "Mark Completed",
          description:
            "Marks the appointment as done. Use this once the booking has happened.",
          tone: "primary",
        },
        {
          next: "no_show",
          label: "Mark as No-show",
          description:
            "Records that the customer didn't turn up. Use this if no payment is collected for the missed appointment.",
          tone: "warning",
        },
        {
          next: "pending",
          label: "Revert to Pending",
          description:
            "Moves the booking back to Pending. Use this to undo a manual confirm. Existing payments stay attached.",
          tone: "default",
        },
      ];
    case "completed":
      return [
        {
          next: "confirmed",
          label: "Reopen as Confirmed",
          description: "Reopens this completed booking as Confirmed.",
          tone: "default",
        },
      ];
    case "no_show":
      return [
        {
          next: "confirmed",
          label: "Reopen as Confirmed",
          description: "Reopens this no-show booking as Confirmed.",
          tone: "default",
        },
      ];
    case "canceled":
      return [
        {
          next: "pending",
          label: "Reopen as Pending",
          description:
            "Reopens this canceled booking as Pending. The customer's slot is restored.",
          tone: "default",
        },
        {
          next: "confirmed",
          label: "Reopen as Confirmed",
          description:
            "Reopens this canceled booking and immediately marks it Confirmed.",
          tone: "primary",
        },
      ];
    default:
      return [];
  }
}

export function useChangeBookingStatus(bookingId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (status: BookingStatus) =>
      unwrap(
        await api.PATCH("/api/v1/bookings/{id}", {
          params: { path: { id: bookingId! } },
          body: { status } as unknown as { status: string },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking", bookingId] });
      qc.invalidateQueries({ queryKey: ["booking-history", bookingId] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
    },
  });
}
